import React, { useState, useMemo, useRef, useEffect } from "react";
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import html2canvas from "html2canvas";
import ShotMapCanvas from "./ShotMapCanvas";
import ShotMapLegend from "./ShotMapLegend";
import { useShotMarkers } from "../hooks/useShotMarkers";
import "./ShotMapWithFilters.css";

const sortByPreferredOrder = (values, preferredOrder) => {
  const seen = new Set();
  const out = [];
  preferredOrder.forEach((v) => {
    if (values.includes(v) && !seen.has(v)) {
      out.push(v);
      seen.add(v);
    }
  });
  values.forEach((v) => {
    if (!seen.has(v)) out.push(v);
  });
  return out;
};

const formatShotTypeLabel = (value) =>
  value === "All" ? value : String(value || "").replace(/\s*attempt$/i, "");

export default function ShotMapWithFilters({ shotsData = [], game }) {
  const homeTeam = game?.HomeTeam || "";
  const awayTeam = game?.AwayTeam || "";

  // -- Filters state --
  const [team, setTeam] = useState("");
  const [shotType, setShotType] = useState("All");
  const [setPlay, setSetPlay] = useState("All");
  const [gameHalf, setGameHalf] = useState("All");
  const [shotOutcome, setShotOutcome] = useState("All");
  const [playerName, setPlayerName] = useState("All");
  const [shotPressure, setShotPressure] = useState("All");
  const [shotMethod, setShotMethod] = useState("All");
  const mapExportRef = useRef(null);

  const teamShotCounts = useMemo(
    () =>
      shotsData.reduce((acc, s) => {
        const t = s.teamShot;
        if (t) acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {}),
    [shotsData],
  );

  const teams = useMemo(() => {
    const fromShots = [...new Set(shotsData.map((s) => s.teamShot).filter(Boolean))];
    const preferred = [homeTeam, awayTeam].filter(Boolean);
    return [...new Set([...preferred, ...fromShots])];
  }, [shotsData, homeTeam, awayTeam]);

  const getDefaultTeam = useMemo(() => {
    if (teamShotCounts[homeTeam] > 0) return homeTeam;
    if (teamShotCounts[awayTeam] > 0) return awayTeam;
    if (homeTeam && teams.includes(homeTeam)) return homeTeam;
    if (awayTeam && teams.includes(awayTeam)) return awayTeam;
    return teams[0] || "";
  }, [teamShotCounts, homeTeam, awayTeam, teams]);

  useEffect(() => {
    if (!teams.length) {
      if (team !== "") setTeam("");
      return;
    }
    if (!team || !teams.includes(team)) {
      setTeam(getDefaultTeam);
    }
  }, [team, teams, getDefaultTeam]);

  const resetFilters = () => {
    setTeam(getDefaultTeam);
    setShotType("All");
    setSetPlay("All");
    setGameHalf("All");
    setShotOutcome("All");
    setPlayerName("All");
    setShotPressure("All");
    setShotMethod("All");
  };

  // -- Dropdown options --
  const types = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotType))],
    [shotsData],
  );
  // ### Refactored to use static choices of "All, open play, dead ball"
  /*
  const plays = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.setPlay))],
    [shotsData],
  );
  */
  
  const plays = ["All", "Open Play", "Dead Ball"]


  const halves = ["All", "1", "2", "3"];
  const outcomes = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotOutcome))],
    [shotsData],
  );
  console.log(shotsData[0])
  const players = useMemo(() => {
    const filteredForPlayers = shotsData.filter((s) => {
      if (team && s.teamShot !== team) return false;
      if (shotType !== "All" && s.shotType !== shotType) return false;
      //if (setPlay !== "All" && s.setPlay !== setPlay) return false;
      //set play 
      /*
      if (setPlay !== "All"){
        if (setPlay === "Open Play" && s.setPlay !== "Open Play") {
          return false
        }
      }
        */

      if (gameHalf !== "All" && String(s.gameHalf) !== gameHalf) return false;
      if (shotOutcome !== "All" && s.shotOutcome !== shotOutcome) return false;
      if (shotPressure !== "All" && s.shotPressure !== shotPressure) return false;
      if (shotMethod !== "All" && s.shotMethod !== shotMethod) return false;
      return Boolean(s.playerName);
    });
    const uniquePlayers = [...new Set(filteredForPlayers.map((s) => s.playerName))]
      .sort((a, b) => String(a).localeCompare(String(b)));
    return ["All", ...uniquePlayers];
  }, [
    shotsData,
    team,
    shotType,
    setPlay,
    gameHalf,
    shotOutcome,
    shotPressure,
    shotMethod,
  ]);
  const pressures = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotPressure))],
    [shotsData],
  );
  const methods = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotMethod))],
    [shotsData],
  );
  const orderedTypes = useMemo(
    () =>
      sortByPreferredOrder(types, [
        "All",
        "Goal attempt",
        "Point attempt",
        "2 Point attempt",
      ]),
    [types],
  );
  const orderedPressures = useMemo(
    () => sortByPreferredOrder(pressures, ["All", "Low", "Medium", "High"]),
    [pressures],
  );
  const orderedMethods = useMemo(
    () =>
      sortByPreferredOrder(methods, [
        "All",
        "Hand",
        "Left foot",
        "Right foot",
      ]),
    [methods],
  );

  useEffect(() => {
    if (playerName !== "All" && !players.includes(playerName)) {
      setPlayerName("All");
    }
  }, [playerName, players]);

  // -- Apply filters --
  const filtered = useMemo(
    () =>
      shotsData.filter((s) => {
        if (team && s.teamShot !== team) return false;
        if (shotType !== "All" && s.shotType !== shotType) return false;
        //if (setPlay !== "All" && s.setPlay !== setPlay) return false;
        if (setPlay !== "All"){
          if (setPlay === "Open Play" && s.setPlay !== "Open Play"){
            return false
          }
          else if (setPlay === "Dead Ball" && s.setPlay === "Open Play"){
            return false
          }
        }
        if (gameHalf !== "All" && String(s.gameHalf) !== gameHalf) return false;
        if (shotOutcome !== "All" && s.shotOutcome !== shotOutcome)
          return false;
        if (playerName !== "All" && s.playerName !== playerName) return false;
        if (shotPressure !== "All" && s.shotPressure !== shotPressure)
          return false;
        if (shotMethod !== "All" && s.shotMethod !== shotMethod) return false;
        return true;
      }),
    [
      shotsData,
      team,
      shotType,
      setPlay,
      gameHalf,
      shotOutcome,
      playerName,
      shotPressure,
      shotMethod,
    ],
  );

  const { markers, summary } = useShotMarkers(filtered);

  const handleDownloadMap = async () => {
    if (!mapExportRef.current) return;
    const canvas = await html2canvas(mapExportRef.current, {
      backgroundColor: null,
      scale: 2,
    });
    const link = document.createElement("a");
    const safeTeam = String(team || "team")
      .trim()
      .replace(/[^\w-]+/g, "_");
    link.download = `shot-map-${safeTeam}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <Row>
          {/* -- FILTERS -- */}
          <Col md={3} className="filters">
            <Form.Group className="mb-2">
              <Form.Label>Select Team</Form.Label>
              <div>
                {teams.map((t) => (
                  <Form.Check
                    key={t}
                    inline
                    type="radio"
                    id={`shot-map-team-${t}`}
                    name="shotMapTeam"
                    label={t}
                    value={t}
                    checked={team === t}
                    onChange={(e) => setTeam(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Shot Type</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedTypes.map((t) => (
                  <Form.Check
                    key={t}
                    type="radio"
                    id={`shot-map-type-${t}`}
                    name="shotMapType"
                    label={formatShotTypeLabel(t)}
                    value={t}
                    checked={shotType === t}
                    onChange={(e) => setShotType(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Set Play</Form.Label>
              <Form.Select
                value={setPlay}
                onChange={(e) => setSetPlay(e.target.value)}
              >
                {plays.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Game Half</Form.Label>
              <div>
                {halves.map((h) => (
                  <Form.Check
                    key={h}
                    inline
                    type="radio"
                    id={`shot-map-half-${h}`}
                    name="shotMapHalf"
                    label={h}
                    value={h}
                    checked={gameHalf === h}
                    onChange={(e) => setGameHalf(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Outcome</Form.Label>
              <Form.Select
                value={shotOutcome}
                onChange={(e) => setShotOutcome(e.target.value)}
              >
                {outcomes.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Player</Form.Label>
              <Form.Select
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
              >
                {players.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Pressure</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedPressures.map((p) => (
                  <Form.Check
                    key={p}
                    type="radio"
                    id={`shot-map-pressure-${p}`}
                    name="shotMapPressure"
                    label={p}
                    value={p}
                    checked={shotPressure === p}
                    onChange={(e) => setShotPressure(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Method</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedMethods.map((m) => (
                  <Form.Check
                    key={m}
                    type="radio"
                    id={`shot-map-method-${m}`}
                    name="shotMapMethod"
                    label={m}
                    value={m}
                    checked={shotMethod === m}
                    onChange={(e) => setShotMethod(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Button variant="warning" onClick={resetFilters} className="w-100">
              Reset Filters
            </Button>
            <Button
              variant="outline-light"
              onClick={handleDownloadMap}
              className="w-100 mt-2"
            >
              Download Map
            </Button>
          </Col>
          <Col md={9}>
            <div ref={mapExportRef} className="map-export">
              <Row className="g-0">
                {/* -- PITCH + MARKERS -- */}
                <Col md={9} className="pitch-wrapper">
                  <ShotMapCanvas
                    markers={markers}
                    summaryText={`${team}: ${summary.total} Shots, xPoints ${summary.xp.toFixed(2)}, Points ${summary.pts}, Efficiency ${summary.eff}, Conversion ${Math.round(summary.conversionRate * 100)}%`}
                  />
                </Col>

                {/* -- LEGEND -- */}
                <Col md={3}>
                  <ShotMapLegend />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}




